import React from 'react';
import OrderList from '../../../components/AdvanceMessagingComponent/OrderList/OrderList';
import ProductList from '../../../components/AdvanceMessagingComponent/ProductList/ProductList';
import {
  ICBBaseResponse,
  ICBCancellationConfirmationResponse,
  ICBGetMessagesResponse,
  ICBGlobalExceptionResponse,
  ICBIntentResponse,
  ICBMoneyBackResponse,
  ICBOrderListResponse,
  ICBProductListResponse,
  ICBProductTrackingResponse,
  ICBStaticMessageResponse,
  IPriceObject,
} from '../../../interfaces/chatbot-api-interfaces';
import { IMessage } from '../../../interfaces/component-interfaces';
import { IOrderData, IOrderProductData } from '../../../interfaces/order-tracking-flow-interfaces';
import { util } from '../../miaw/common';
import { DISPLAY_NAME } from '../../../config/app-config';
import ButtonGroup from '../../../components/AdvanceMessagingComponent/ButtonGroup/ButtonGroup';
import { getChatbotSessionInfo } from '../api-utils';
import NonLoggedInOrderForm from '../../../components/NonLoggedInOrderForm/NonLoggedInOrderForm';
import { ISendMessage } from '../../../hooks/chatbot/useCBChatMessage';
import { handleMessageWithDifferentQuery, handleNonLoggedInOrderMessage, handleOrderSelect, handleProductSelect, handleTextMessage } from './request-handlers';
import { websiteBaseURLs } from '../../../config/url-config';
import { getChatSessionType } from '../../../utils/session-storage-utils';
import { IUrlData } from '../../../interfaces/app-interfaces';

const getDefaultURLMapping = (locale: string) => {
  const baseURL = websiteBaseURLs[locale] || websiteBaseURLs['en-us'];
  return {
    'Track Order,': `${baseURL}/oh/`,
    'Get design support,': `${baseURL}/design`,
    'Visit Help Center.': `${baseURL}/customer-care/help-centre/`,
  };
};
const getAgentButton = () => {
  return <ButtonGroup actionList={[{ label: 'Chat with a specialist', value: 'agentConnect' }]} type="buttons" />;
};

type ButtonGroupType = 'transferToAgent' | 'nonLoggedInPreChatWithOrderNumber';

const tranferToAgent = (typeOfFLow: ButtonGroupType) => {
  return <ButtonGroup actionList={[]} type="buttons" typeOfFLow={typeOfFLow} />;
};

export const showAgentButton = (data: string) => {
  let showButton = false;
  if (data.toLowerCase().includes('specialist') || data.toLowerCase().includes('agent')) {
    showButton = true;
  }
  if (data.toLowerCase().includes('offline')) showButton = false;
  return showButton;
};

const getMessageList = (
  locale: string,
  data: string[],
  responseURLMapping?: { url: string; label: string }[],
  isChatDisabled = false,
): {
  updatedMessageList: string[];
  showAgent: boolean;
  urlMapping: { url: string; label: string }[];
  isSpecialistConnect: boolean;
} => {
  let showButton = false;
  const messageList: string[] = [];
  const urlMapping: { url: string; label: string }[] = [];
  let isSpecialistConnect = false;

  data.forEach((msg) => {
    // Check if the message matches any keys in defaultURLMapping
    const defaultURLMapping = getDefaultURLMapping(locale);
    const matchedKeys = Object.keys(defaultURLMapping).filter((key) => msg?.toLowerCase().includes(key.toLowerCase()));

    if (matchedKeys.length > 0) {
      // Replace all matched keys with their corresponding URLs
      let replacedMsg = msg;
      matchedKeys.forEach((matchedKey) => {
        const escapedKey = matchedKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // ✅ escape before using in regex
        const regex = new RegExp(escapedKey, 'ig');
        replacedMsg = replacedMsg.replace(regex, `\n${defaultURLMapping[matchedKey]}`);
        // Store the URL mapping for later use if needed
        urlMapping.push({
          url: defaultURLMapping[matchedKey],
          label: matchedKey,
        });
      });
      messageList.push(replacedMsg);
      return;
    }

    /** In order to handle Links within msgs, replace the title with urls,
     * in the msg component these links will be hyperlinked and title will be used
     */
    let updatedMsg = msg;
    responseURLMapping?.forEach((item) => {
      if (msg.toLowerCase().includes(item.label.toLowerCase())) {
        const escapedLabel = item.label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        // Handle case insensitive replacement
        const regex = new RegExp(escapedLabel, 'ig');
        // Only replace if there is no _ surrounding either side of the label
        if (!msg.includes('_' + item.label + '_') && !msg.includes('_' + item.label) && !msg.includes(item.label + '_')) {
          updatedMsg = updatedMsg.replace(regex, item.url);
        }
      }
    });

    if (updatedMsg === 'SPECIALIST CONNECTED') {
      isSpecialistConnect = true;
    }
    if (isChatDisabled) {
      // Skip showing agent button if chat is disabled
      showButton = false;
    } else {
      if ((updatedMsg.toLowerCase().includes('specialist') || updatedMsg.toLowerCase().includes('agent')) && updatedMsg !== 'SPECIALIST CONNECTED') {
        if (getChatSessionType() !== 'miawChatSession') {
          showButton = true;
        }
      }
      if (updatedMsg.toLowerCase().includes('offline')) showButton = false;

      updatedMsg = updatedMsg.replace(/You can track it here:/g, '').trim();
      if (updatedMsg.includes('[Non-Logged In User] -- ')) {
        updatedMsg = updatedMsg.replace('[Non-Logged In User] -- ', '').trim();
      }
    }
    messageList.push(updatedMsg);
  });
  return { updatedMessageList: messageList, showAgent: showButton, urlMapping, isSpecialistConnect: isSpecialistConnect };
};

export const transformChatbotResponse = (
  locale: string,
  fetchedData: ICBBaseResponse,
  msgId: string,
  timestamp?: number,
  isRetrivedData = false,
  isLastMessage = false,
  globalExceptionCount?: number,
  isChatDisabled = false,
): IMessage[] => {
  let message: IMessage;
  const messageType = fetchedData?.response?.data?.type;
  if (messageType) {
    switch (messageType) {
      case 'order_list':
        if ((fetchedData as ICBOrderListResponse).response.data.is_guest_customer) {
          message = handleNonLoggedInOrderTrackingResponse(locale, fetchedData as ICBOrderListResponse, timestamp, isRetrivedData, isLastMessage);
        } else {
          message = handleOrderResponse(locale, fetchedData as ICBOrderListResponse, timestamp, isRetrivedData, isLastMessage, isChatDisabled);
        }
        break;
      case 'user_sub_intent':
      case 'user_intent':
        message = handleIntentResponse(locale, fetchedData as ICBIntentResponse, timestamp, isRetrivedData, isLastMessage, isChatDisabled);
        break;
      case 'money_back_mode':
        message = handleMoneyBackResponse(locale, fetchedData as ICBMoneyBackResponse, timestamp, isRetrivedData, isLastMessage);
        break;
      case 'products_cancellable_status':
        if (
          (fetchedData as ICBProductListResponse).response.data.is_guest_customer &&
          (fetchedData as ICBProductListResponse).response.data.intent === 'request cancellation'
        ) {
          message = handleNonLoggedInOrderTrackingResponse(locale, fetchedData as ICBOrderListResponse, timestamp, isRetrivedData, isLastMessage);
        } else {
          message = handleCancelOrderProductsResponse(locale, fetchedData as ICBProductListResponse, timestamp, isRetrivedData, isLastMessage, isChatDisabled);
        }
        break;
      case 'product_list':
        message = handleOrderProductsResponse(locale, fetchedData as ICBProductListResponse, timestamp, isRetrivedData, isLastMessage, isChatDisabled);
        break;
      case 'product_tracking':
        message = handleProductTrackingResponse(locale, fetchedData as ICBProductTrackingResponse, timestamp, isRetrivedData, isLastMessage, isChatDisabled);
        break;
      case 'cancellation_confirmation':
        message = handleCancelConfirmationResponse(
          locale,
          fetchedData as ICBCancellationConfirmationResponse,
          timestamp,
          isRetrivedData,
          isLastMessage,
          isChatDisabled,
        );
        break;
      case 'escalate_to_agent':
        message = handleSpecialistEscalation(locale, fetchedData as ICBStaticMessageResponse, timestamp, isRetrivedData, isLastMessage);
        break;
      case 'text_message':
      default:
        message = handleGenericMessageResponse(locale, fetchedData as ICBStaticMessageResponse, timestamp, isRetrivedData, isLastMessage, isChatDisabled);
        break;
    }
  } else {
    if (fetchedData.response.response === 'Error') {
      // Handle error case
      message = handleGlobalException(locale, fetchedData as ICBGlobalExceptionResponse, globalExceptionCount, timestamp, isRetrivedData, isChatDisabled);
    } else {
      message = handleGenericMessageResponse(locale, fetchedData as ICBStaticMessageResponse, timestamp, isRetrivedData, isLastMessage, isChatDisabled);
    }
  }
  /** Update message Id to either API response or to restId from frontend */
  message = { ...message, id: fetchedData?.message_id || msgId };

  return [message];
};
const currencyMapping = {
  AUD: '$',
  CAD: '$',
  EUR: '€',
  GBP: '£',
  SGD: '$',
  USD: '$',
  NZD: '$',
};
export const getPrice = (price: IPriceObject): string => {
  const value = price.centAmount / Math.pow(10, price.fractionDigits);
  return `${currencyMapping[price.currencyCode] || price.currencyCode}${value}`;
};

export const getOrderList = (locale: string, data: ICBOrderListResponse): IOrderData[] => {
  const orderList: IOrderData[] = data.response?.data?.orders?.map((order) => ({
    orderId: order.orderNumber,
    orderNumber: order.orderNumber,
    orderDate: new Date(order.orderCreationDate).getTime(),
    displayPrice: getPrice(order.orderTotal),
    orderItems: order.product_urls.map((url) => ({ itemId: url.label, imageUrl: url.url })),
  }));
  return orderList;
};

const handleIntentResponse = (
  locale: string,
  data: ICBIntentResponse,
  timestamp?: number,
  isRetrivedData = false,
  isLastMessage = false,
  isChatDisabled = false,
): IMessage => {
  const intents: string[] = data.response?.data?.intents;
  const { updatedMessageList, showAgent, urlMapping } = getMessageList(locale, data?.response?.messages, data.response?.data?.urls, isChatDisabled);
  const message: IMessage = {
    id: util.generateUUID(),
    messageContent: updatedMessageList,
    type: 'chatbot',
    timestamp: timestamp ? new Date(timestamp).getTime() : new Date().getTime(),
    senderDisplayName: DISPLAY_NAME.CHATBOT,
    isMessageRead: isRetrivedData ? true : false,
    additionalComponents:
      (!isRetrivedData || isLastMessage) &&
      (intents.length > 0 ? (
        <ButtonGroup
          actionList={intents.map((int) => {
            return { label: int, value: int };
          })}
          postion="end"
          type="buttons"
        />
      ) : showAgent ? (
        getAgentButton()
      ) : undefined),
    urlMapping,
    customMessageType: 'orderIntentResponseMessage',
    showFeedback: true,
  };
  return message;
};

const handleMoneyBackResponse = (
  locale: string,
  data: ICBMoneyBackResponse,
  timestamp?: number,
  isRetrivedData = false,
  isLastMessage = false,
  // isChatDisabled = false,
): IMessage => {
  const moneyBackModes = data?.response?.data?.money_back_modes;
  const is_remediation_available = data?.response?.data?.is_remediation_available;
  // const is_remediation_available = false;

  const { updatedMessageList, showAgent, urlMapping } = getMessageList(locale, data?.response?.messages, data.response?.data?.urls);
  const hasMoneyOptionNotAvailable = moneyBackModes.some((item) => item.is_option_available === false);
  if (hasMoneyOptionNotAvailable) {
    updatedMessageList.push('**Note:** Some options may not be available if you’ve already made a request or because of your payment method.');
  }

  const message: IMessage = {
    id: util.generateUUID(),
    messageContent:
      is_remediation_available == false ? 'Thank you! I’ll connect you with a specialist to review your case and help resolve it.' : updatedMessageList,
    type: 'chatbot',
    timestamp: timestamp ? new Date(timestamp).getTime() : new Date().getTime(),
    senderDisplayName: DISPLAY_NAME.CHATBOT,
    isMessageRead: isRetrivedData ? true : false,
    additionalComponents:
      (!isRetrivedData || isLastMessage) &&
      (!is_remediation_available ? (
        tranferToAgent('transferToAgent')
      ) : moneyBackModes.length ? (
        <ButtonGroup
          actionList={moneyBackModes.map((item) => {
            return { label: item.label, description: item.description, value: item.label, isOptionAvailable: item.is_option_available };
          })}
          postion="start"
          type="tiles-horizontal"
          typeOfFLow="moneyBack"
        />
      ) : showAgent ? (
        getAgentButton()
      ) : undefined),
    urlMapping,
    customMessageType: 'moneyBackModeMessage',
    showFeedback: true,
  };
  return message;
};

const handleSpecialistEscalation = (
  locale: string,
  data: ICBStaticMessageResponse,
  timestamp?: number,
  isRetrivedData = false,
  isLastMessage = false,
): IMessage => {
  const message: IMessage = {
    id: util.generateUUID(),
    messageContent: 'Thank you! I’ll connect you with a specialist to review your case and help resolve it.',
    type: 'chatbot',
    timestamp: timestamp ? new Date(timestamp).getTime() : new Date().getTime(),
    senderDisplayName: DISPLAY_NAME.CHATBOT,
    isMessageRead: isRetrivedData ? true : false,
    additionalComponents: (!isRetrivedData || isLastMessage) && tranferToAgent('transferToAgent'),
    customMessageType: 'escalationResponse',
    showFeedback: true,
  };
  return message;
};

const handleCancelOrderProductsResponse = (
  locale: string,
  data: ICBProductListResponse,
  timestamp?: number,
  isRetrivedData = false,
  isLastMessage = false,
  isChatDisabled = false,
): IMessage => {
  const productItem: IOrderProductData[] = data?.response?.data?.products?.map((product) => ({
    name: product.productName,
    displayPrice: getPrice(product.totalDiscountedPrice),
    itemId: product.productId,
    imagURL: product.livePreviewUrl,
    is_cancellable: product.is_cancellable,
    status: product.status ?? 'In production',
  }));

  let messages = data?.response?.messages;

  const isProductNotEligibleForCancellation = data?.response?.data?.is_products_not_eligible_for_cancellation || data.response.data.is_order_not_cancellable; // TODO
  if (isProductNotEligibleForCancellation) {
    messages = [
      `I'm sorry, but none of the products in this order can be cancelled because they've already been shipped or delivered.`,
      `Is there anything else I can help with?`,
    ];
  }
  const { updatedMessageList, showAgent, urlMapping } = getMessageList(locale, messages, data.response?.data?.urls, isChatDisabled);

  const message: IMessage = {
    id: util.generateUUID(),
    messageContent: updatedMessageList,
    type: 'chatbot',
    timestamp: timestamp ? new Date(timestamp).getTime() : new Date().getTime(),
    senderDisplayName: DISPLAY_NAME.CHATBOT,
    isMessageRead: isRetrivedData ? true : false,
    additionalComponents:
      (!isRetrivedData || isLastMessage) &&
      (productItem.length > 0 && !isProductNotEligibleForCancellation ? (
        <ProductList items={productItem} orderId={data.response.data.orderID} sender="chatbot" isCancellationFlow={true} />
      ) : showAgent ? (
        getAgentButton()
      ) : undefined),
    urlMapping,
    customMessageType: 'productListMessage',
    showFeedback: true,
  };

  return message;
};

const handleCancelConfirmationResponse = (
  locale: string,
  data: ICBCancellationConfirmationResponse,
  timestamp?: number,
  isRetrivedData = false,
  isLastMessage = false,
  isChatDisabled = false,
): IMessage => {
  const { updatedMessageList, showAgent, urlMapping } = getMessageList(locale, data?.response?.messages, data.response?.data?.urls, isChatDisabled);
  const message: IMessage = {
    id: util.generateUUID(),
    messageContent: updatedMessageList,
    type: 'chatbot',
    timestamp: timestamp ? new Date(timestamp).getTime() : new Date().getTime(),
    senderDisplayName: DISPLAY_NAME.CHATBOT,
    isMessageRead: isRetrivedData ? true : false,
    additionalComponents:
      (!isRetrivedData || isLastMessage) && showAgent ? (
        getAgentButton()
      ) : (
        <ButtonGroup
          actionList={[
            { label: 'No, thank you', value: 'No, thank you' },
            { label: 'Yes', value: 'Yes' },
          ]}
          type="buttons"
        />
      ),
    urlMapping,
    customMessageType: 'orderOptionsMessage',
    showFeedback: true,
  };
  return message;
};

const handleGlobalException = (
  locale: string,
  data: ICBGlobalExceptionResponse,
  globalExceptionCount = 0,
  timestamp?: number,
  isRetrivedData = false,
  isChatDisabled = false,
): IMessage => {
  let message: IMessage;
  console.log('Global Exception Count:', globalExceptionCount);

  if (globalExceptionCount && globalExceptionCount > 1) {
    const genericMessage = [`Our specialists are offline right now. In the meantime, you can visit Help Center or search for articles right here in the chat.`];
    // const hurricaneMessage = [
    //   `Hurricane Melissa is impacting one of our contact centers, so I can’t connect you with a specialist for additional support right now. `,
    //   `You can still get help by sending us an email. Our team worldwide is doing their best to assist everyone as quickly as possible. There may be some delays, so thanks for your patience and understanding.`,
    // ];
    message = {
      id: util.generateUUID(),
      messageContent: isChatDisabled
        ? //  locale === 'en-us' || locale === 'en-ca'
          // ? hurricaneMessage :
          genericMessage
        : `I'm sorry, I'm having some issues on my end. Would you like to chat with a specialist?`,
      type: 'chatbot',
      timestamp: timestamp ? new Date(timestamp).getTime() : new Date().getTime(),
      senderDisplayName: DISPLAY_NAME.CHATBOT,
      isMessageRead: isRetrivedData ? true : false,
      additionalComponents: !isRetrivedData && !isChatDisabled && getAgentButton(),
      customMessageType: 'escalationResponse',
      showFeedback: true,
    };
  } else {
    // Provide a default message for other cases
    message = {
      id: util.generateUUID(),
      messageContent: [`Sorry, it looks like there's a technical issue and my thinking is running a bit slow right now. Would you like me to try again?`],
      type: 'chatbot',
      timestamp: timestamp ? new Date(timestamp).getTime() : new Date().getTime(),
      senderDisplayName: DISPLAY_NAME.CHATBOT,
      isMessageRead: isRetrivedData ? true : false,
      additionalComponents: (
        <ButtonGroup
          actionList={[
            { label: 'No', value: 'No' },
            { label: 'Yes', value: 'Yes' },
          ]}
          type="buttons"
          typeOfFLow="globalException"
        />
      ),
      customMessageType: 'globalExceptionMessage',
      showFeedback: true,
    };
  }
  return message;
};

const handleOrderResponse = (
  locale: string,
  data: ICBOrderListResponse,
  timestamp?: number,
  isRetrivedData = false,
  isLastMessage = false,
  isChatDisabled = false,
): IMessage => {
  const orderList: IOrderData[] = getOrderList(locale, data);
  const { updatedMessageList, showAgent, urlMapping } = getMessageList(locale, data?.response?.messages, data.response?.data?.urls, isChatDisabled);
  const message: IMessage = {
    id: util.generateUUID(),
    messageContent: updatedMessageList,
    type: 'chatbot',
    timestamp: timestamp ? new Date(timestamp).getTime() : new Date().getTime(),
    senderDisplayName: DISPLAY_NAME.CHATBOT,
    isMessageRead: isRetrivedData ? true : false,
    additionalComponents:
      (!isRetrivedData || isLastMessage) &&
      (orderList.length > 0 ? (
        <OrderList orders={orderList} ordersCount={data.response.data.total_orders} sender="chatbot" />
      ) : showAgent ? (
        getAgentButton()
      ) : undefined),
    urlMapping,
    customMessageType: 'orderListMessage',
    showFeedback: true,
  };

  return message;
};

const handleOrderProductsResponse = (
  locale: string,
  data: ICBProductListResponse,
  timestamp?: number,
  isRetrivedData = false,
  isLastMessage = false,
  isChatDisabled = false,
): IMessage => {
  const productItem: IOrderProductData[] = data?.response?.data?.products?.map((product) => ({
    name: product.productName,
    displayPrice: getPrice(product.totalDiscountedPrice),
    itemId: product.productId,
    imagURL: product.livePreviewUrl,
  }));
  const { updatedMessageList, showAgent, urlMapping } = getMessageList(locale, data?.response?.messages, data.response?.data?.urls, isChatDisabled);

  const message: IMessage = {
    id: util.generateUUID(),
    messageContent: updatedMessageList,
    type: 'chatbot',
    timestamp: timestamp ? new Date(timestamp).getTime() : new Date().getTime(),
    senderDisplayName: DISPLAY_NAME.CHATBOT,
    isMessageRead: isRetrivedData ? true : false,
    additionalComponents:
      (!isRetrivedData || isLastMessage) &&
      (productItem.length > 0 ? (
        <ProductList items={productItem} orderId={data.response.data.orderID} sender="chatbot" />
      ) : showAgent ? (
        getAgentButton()
      ) : undefined),
    urlMapping,
    customMessageType: 'productListMessage',
    showFeedback: true,
  };

  return message;
};

const handleProductTrackingResponse = (
  locale: string,
  data: ICBProductTrackingResponse,
  timestamp?: number,
  isRetrivedData = false,
  isLastMessage = false,
  isChatDisabled = false,
): IMessage => {
  const { updatedMessageList, showAgent, urlMapping } = getMessageList(locale, data?.response?.messages, data.response?.data?.urls, isChatDisabled);
  let updatedURLMapping: { label: string; url: string }[] = [];

  const trackingInfo = data?.response?.data?.tracking_info;
  if (urlMapping.length > 0) {
    updatedURLMapping = [...urlMapping];
  }
  trackingInfo?.forEach((info) => {
    info?.fulfillmentData?.forEach((fulfillment) => {
      fulfillment?.urls?.forEach((url) => {
        updatedURLMapping.push({ label: url.label, url: url.url });
      });
    });
  });
  const message: IMessage = {
    id: util.generateUUID(),
    messageContent: updatedMessageList,
    type: 'chatbot',
    timestamp: timestamp ? new Date(timestamp).getTime() : new Date().getTime(),
    isMessageRead: timestamp ? true : false,
    senderDisplayName: DISPLAY_NAME.CHATBOT,
    urlMapping: updatedURLMapping,
    additionalComponents: (!isRetrivedData || isLastMessage) && (showAgent ? getAgentButton() : undefined),
    showFeedback: true,
  };
  return message;
};

const handleGenericMessageResponse = (
  locale: string,
  data: ICBStaticMessageResponse,
  timestamp?: number,
  isRetrivedData = false,
  isLastMessage = false,
  isChatDisabled = false,
): IMessage => {
  console.log('Generic message response:', data, timestamp);
  let messageContent: string = '';

  if (typeof data.response === 'string') messageContent = data?.response;

  const { showAgent, updatedMessageList, urlMapping, isSpecialistConnect } = getMessageList(
    locale,
    data?.response?.messages,
    data.response?.data?.urls,
    isChatDisabled,
  );

  const updateURLMapping: IUrlData[] = [...urlMapping];

  /** For all URLs, replace the TITLE Keyword */

  if (data.response?.data?.urls) {
    const agentName = data.response?.agent_name;
    const openInSameTab = agentName === 'ProductAgent';
    console.log('agentName and openInSameTab:', agentName, openInSameTab);
    data.response.data.urls.forEach((url) => {
      /** If link enclosed between {**}, the link should be bold */
      let isBold = false;
      updatedMessageList.forEach((msg, index) => {
        if (msg.includes(url.url)) {
          if (msg.includes(`**${url.url}**`)) {
            updatedMessageList[index] = msg.replace(`**${url.url}**`, url.url);
            isBold = true;
          }
        }
      });
      updateURLMapping.push({
        label: url.label
          .replace(/_URL_TITLE/g, '')
          .toLowerCase()
          .split('_')
          .join(' '),
        url: url.url,
        isBold: isBold,
        openInSameTab: openInSameTab,
      });
    });
  }

  if (data?.response.promo_code_details) {
    if (data?.response.promo_code_details?.is_promo_url_present) {
      updateURLMapping.push({
        url: data?.response?.promo_code_details?.promo_code_url,
        label: data?.response?.promo_code_details?.promo_code_label,
      });
    }
  }
  const message: IMessage = {
    id: util.generateUUID(),
    messageContent: isSpecialistConnect ? '' : updatedMessageList || messageContent,
    type: 'chatbot',
    timestamp: timestamp ? new Date(timestamp).getTime() : new Date().getTime(),
    senderDisplayName: DISPLAY_NAME.CHATBOT,
    isMessageRead: isRetrivedData ? true : false,
    additionalComponents:
      (!isRetrivedData || isLastMessage) && (showAgent ? getAgentButton() : isSpecialistConnect ? tranferToAgent('transferToAgent') : undefined),
    urlMapping: updateURLMapping,
    showFeedback: true,
  };

  return message;
};

export const handleGetMessageList = (
  locale: string,
  data: ICBGetMessagesResponse,
  token: string,
): {
  messages: IMessage[];
  discussionId: string;
  sessionId: string;
} => {
  let messages: IMessage[] = [];
  const msgCount = data.data.messages.length;
  // Sort messages by timestamp
  const modifiedData = data.data.messages.sort((a, b) => new Date(a.created).getTime() - new Date(b.created).getTime());

  modifiedData.forEach((msg, index) => {
    if (msg.role === 'user') {
      let message: IMessage;
      if (msg.content_json) {
        const content = msg.content_json as ISendMessage;
        switch (content.type) {
          case 'order_select':
            message = handleOrderSelect(content, new Date(msg.created).getTime()).userMessage;
            break;
          case 'product_select':
            message = handleProductSelect(content, new Date(msg.created).getTime()).userMessage;
            break;
          case 'text_message_with_different_query':
            message = handleMessageWithDifferentQuery(content, new Date(msg.created).getTime()).userMessage;
            break;
          case 'non_logged_in_order_tracking':
            message = handleNonLoggedInOrderMessage(content, new Date(msg.created).getTime()).userMessage;
            break;
          case 'text_message':
          default:
            message = handleTextMessage(content, new Date(msg.created).getTime()).userMessage;
            break;
        }
      } else {
        message = {
          id: msg.id,
          messageContent: getMessageList(locale, [msg.content])?.updatedMessageList,
          type: 'user',
          timestamp: new Date(msg.created).getTime(),
          isMessageRead: true,
          senderDisplayName: DISPLAY_NAME.USER,
          showFeedback: false,
        };
      }
      messages.push(message);
    } else {
      const transformedData = transformChatbotResponse(
        locale,
        msg.content_json as ICBBaseResponse,
        util.generateUUID(), // For retrieved msg, use a new UUID
        new Date(msg.created).getTime() + 1,
        true,
        index !== 0 && index === msgCount - 1, // ensure that initial message is not marked as last message
      ); // Adding 1 ms to ensure the timestamp is unique

      messages = [...messages, ...transformedData];
    }
  });

  const { discussionId, sessionId } = getChatbotSessionInfo(token);
  return { messages, discussionId: discussionId || '', sessionId: sessionId || '' };
};

export const handleNonLoggedInOrderTrackingResponse = (
  locale: string,
  data: ICBOrderListResponse,
  timestamp?: number,
  isRetrivedData = false,
  isLastMessage = false,
): IMessage => {
  const IsIntentRequestCancelation = data.response?.data?.intent === 'request cancellation';
  const RequestCancelationText =
    'Let’s start with your email and order number.\n\nNote: If you placed your order with an account, sign in first. \n\n<ext>Sign in</ext>';

  const message: IMessage = {
    id: util.generateUUID(),
    messageContent: IsIntentRequestCancelation ? RequestCancelationText : ['Enter your email and order number.'],
    type: 'chatbot',
    timestamp: timestamp ? new Date(timestamp).getTime() : new Date().getTime(),
    senderDisplayName: DISPLAY_NAME.CHATBOT,
    isMessageRead: isRetrivedData ? true : false,
    additionalComponents:
      (!isRetrivedData || isLastMessage) && IsIntentRequestCancelation ? (
        tranferToAgent('nonLoggedInPreChatWithOrderNumber')
      ) : !isRetrivedData || isLastMessage ? (
        <NonLoggedInOrderForm />
      ) : undefined,
    // urlMapping,
    customMessageType: 'orderListMessage',
    showFeedback: true,
  };

  return message;
};
